import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useRFQ } from '../contexts/RFQContext';
import {
  ShoppingCart,
  Globe,
  UserCircle,
  MapTrifold,
  List,
  ForkKnife,
  Buildings,
  SprayBottle,
  Sparkle,
  CaretRight,
  CaretDown,
  SquaresFour,
} from '@phosphor-icons/react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { CATEGORIES, getCategoryTitle } from './CategoryGrid';
import CategoryMegaMenuRightColumn from './CategoryMegaMenuRightColumn';
import { SUBMENU_BY_CATEGORY_ID, getSubmenuLabel } from '../constants/categorySubmenus';
import { SITE_LOGO_SRC, WORKSPACE_LOGIN_URL } from '../constants/branding';
import {
  HOME_SECTION_IDS,
} from '../constants/homeSections';
import SolutionsNavPanel from './SolutionsNavPanel';
import ProcurementModelsNavPanel from './ProcurementModelsNavPanel';
import { scrollToHomeSection } from '../utils/scrollPage';
import './mega-menu/header-mega-menu.css';

const CATEGORY_MENU_ICONS = {
  food: ForkKnife,
  construction: Buildings,
  cleaning: SprayBottle,
  personal_care: Sparkle,
};

/** Dil seçici: bayrak + erişilebilir etiket (emoji bölgesel gösterim) */
const LANGUAGE_OPTIONS = [
  { value: 'en', flag: '🇺🇸', label: 'English' },
  { value: 'fr', flag: '🇫🇷', label: 'Français' },
  { value: 'es', flag: '🇪🇸', label: 'Español' },
  { value: 'ar', flag: '🇸🇦', label: 'العربية' },
];

export default function Header({
  onNavigate,
  currentPage,
  onCategorySelect,
  testSiteBanner = false,
}) {
  const { t, tl, lang, setLang } = useLanguage();
  const { items } = useRFQ();
  const [categoriesPanelOpen, setCategoriesPanelOpen] = useState(false);
  const [solutionsPanelOpen, setSolutionsPanelOpen] = useState(false);
  const [procurementPanelOpen, setProcurementPanelOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileProcurementOpen, setMobileProcurementOpen] = useState(false);
  const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
  const [mobileExpandedId, setMobileExpandedId] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isLg, setIsLg] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  const headerRef = useRef(null);
  const [megaMenuTopPx, setMegaMenuTopPx] = useState(0);
  const [megaClosing, setMegaClosing] = useState(false);
  const categoriesCloseTimerRef = useRef(null);
  const solutionsCloseTimerRef = useRef(null);
  const procurementCloseTimerRef = useRef(null);

  const clearCategoriesCloseTimer = useCallback(() => {
    if (categoriesCloseTimerRef.current) {
      clearTimeout(categoriesCloseTimerRef.current);
      categoriesCloseTimerRef.current = null;
    }
  }, []);

  const scheduleCategoriesClose = useCallback(() => {
    clearCategoriesCloseTimer();
    setMegaClosing(true);
    categoriesCloseTimerRef.current = setTimeout(() => {
      setCategoriesPanelOpen(false);
      setMegaClosing(false);
      categoriesCloseTimerRef.current = null;
    }, 320);
  }, [clearCategoriesCloseTimer]);

  const openCategoriesHover = useCallback(() => {
    clearCategoriesCloseTimer();
    setMegaClosing(false);
    setSolutionsPanelOpen(false);
    setProcurementPanelOpen(false);
    setCategoriesPanelOpen(true);
  }, [clearCategoriesCloseTimer]);

  const clearSolutionsCloseTimer = useCallback(() => {
    if (solutionsCloseTimerRef.current) {
      clearTimeout(solutionsCloseTimerRef.current);
      solutionsCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSolutionsClose = useCallback(() => {
    clearSolutionsCloseTimer();
    solutionsCloseTimerRef.current = setTimeout(() => {
      setSolutionsPanelOpen(false);
      solutionsCloseTimerRef.current = null;
    }, 220);
  }, [clearSolutionsCloseTimer]);

  const openSolutionsHover = useCallback(() => {
    clearSolutionsCloseTimer();
    setCategoriesPanelOpen(false);
    setMegaClosing(false);
    setProcurementPanelOpen(false);
    setSolutionsPanelOpen(true);
  }, [clearSolutionsCloseTimer]);

  const clearProcurementCloseTimer = useCallback(() => {
    if (procurementCloseTimerRef.current) {
      clearTimeout(procurementCloseTimerRef.current);
      procurementCloseTimerRef.current = null;
    }
  }, []);

  const scheduleProcurementClose = useCallback(() => {
    clearProcurementCloseTimer();
    procurementCloseTimerRef.current = setTimeout(() => {
      setProcurementPanelOpen(false);
      procurementCloseTimerRef.current = null;
    }, 220);
  }, [clearProcurementCloseTimer]);

  const openProcurementHover = useCallback(() => {
    clearProcurementCloseTimer();
    setCategoriesPanelOpen(false);
    setMegaClosing(false);
    setSolutionsPanelOpen(false);
    setProcurementPanelOpen(true);
  }, [clearProcurementCloseTimer]);

  useEffect(() => () => clearCategoriesCloseTimer(), [clearCategoriesCloseTimer]);
  useEffect(() => () => clearSolutionsCloseTimer(), [clearSolutionsCloseTimer]);
  useEffect(() => () => clearProcurementCloseTimer(), [clearProcurementCloseTimer]);

  useLayoutEffect(() => {
    if (categoriesPanelOpen) {
      /* Masaüstü: sağ panel doğrudan Food & Beverages; mobilde aynı blok açık. */
      setHoveredCategoryId('food');
      setMobileExpandedId('food');
    }
  }, [categoriesPanelOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const apply = () => setIsLg(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const updateMegaMenuPosition = useCallback(() => {
    const el = headerRef.current;
    if (!el) return;
    setMegaMenuTopPx(Math.round(el.getBoundingClientRect().bottom));
  }, []);

  useLayoutEffect(() => {
    updateMegaMenuPosition();
  }, [categoriesPanelOpen, scrolled, updateMegaMenuPosition]);

  useEffect(() => {
    window.addEventListener('resize', updateMegaMenuPosition);
    window.addEventListener('scroll', updateMegaMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMegaMenuPosition);
      window.removeEventListener('scroll', updateMegaMenuPosition, true);
    };
  }, [updateMegaMenuPosition]);

  const centerNavItems = [
    { key: 'products', label: t('products') },
    { key: 'rfq', label: t('rfqBasket'), shortLabel: t('rfqBasketShort') },
    { key: 'contact', label: t('contact') },
  ];

  const renderNavLabel = (full, short) =>
    short ? (
      <>
        <span className="xl:hidden">{short}</span>
        <span className="hidden xl:inline">{full}</span>
      </>
    ) : (
      full
    );

  const navigateToHomeSection = useCallback(
    (sectionId) => {
      if (currentPage === 'home') {
        if (typeof window !== 'undefined') {
          window.history.replaceState(
            { page: 'home', homeSection: sectionId },
            '',
            `/#${sectionId}`,
          );
        }
        scrollToHomeSection(sectionId);
      } else {
        onNavigate('home', undefined, { homeSection: sectionId });
      }
    },
    [currentPage, onNavigate],
  );

  const goToLiveShipmentTracking = useCallback(() => {
    navigateToHomeSection(HOME_SECTION_IDS.liveShipmentTracking);
  }, [navigateToHomeSection]);

  const handleSolutionClick = useCallback(
    (item) => {
      scheduleSolutionsClose();
      const { action } = item;
      if (action.type === 'section') {
        navigateToHomeSection(action.sectionId);
      } else if (action.type === 'page') {
        onNavigate(action.page);
      } else if (action.type === 'external' && action.href) {
        window.open(action.href, '_blank', 'noopener,noreferrer');
      }
    },
    [navigateToHomeSection, onNavigate, scheduleSolutionsClose],
  );

  const handleProcurementClick = useCallback(
    (item) => {
      scheduleProcurementClose();
      const { action } = item;
      if (action.type === 'section') {
        navigateToHomeSection(action.sectionId);
      } else if (action.type === 'page') {
        onNavigate(action.page);
      } else if (action.type === 'external' && action.href) {
        window.open(action.href, '_blank', 'noopener,noreferrer');
      }
    },
    [navigateToHomeSection, onNavigate, scheduleProcurementClose],
  );

  const pickCategory = onCategorySelect || (() => {});

  const selectCategoryAndClose = (categoryKey, subKey, navSearch = '') => {
    const q = (navSearch || '').trim();
    if (q) {
      pickCategory({ category: categoryKey, subcategory: '', search: q });
    } else if (subKey) {
      pickCategory({ category: categoryKey, subcategory: subKey, search: '' });
    } else if (categoryKey === '' || categoryKey == null) {
      pickCategory('');
    } else {
      pickCategory(categoryKey);
    }
    setMegaClosing(true);
    setTimeout(() => {
      setCategoriesPanelOpen(false);
      setMegaClosing(false);
    }, 320);
  };

  const hoveredCat = CATEGORIES.find((c) => c.id === hoveredCategoryId);
  const hoveredSubs = hoveredCat ? SUBMENU_BY_CATEGORY_ID[hoveredCat.id] || [] : [];

  const menuCapLabel = tl('Menu', 'Menu', 'Menú', 'القائمة');

  /** Mobil: Sheet açık mı (hamburger ile aynı panel). */
  const mobileDrawerOpen = categoriesPanelOpen && !isLg;

  const closeMobileDrawer = useCallback(() => {
    setCategoriesPanelOpen(false);
    setMobileSolutionsOpen(false);
    setMobileProcurementOpen(false);
  }, []);

  const toggleMobileDrawer = useCallback(() => {
    setMegaClosing(false);
    setCategoriesPanelOpen((open) => !open);
  }, []);

  const categoriesMegaMenuBody = (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row" aria-label={t('categories')}>
      <nav className="mega-sidebar flex min-h-[220px] w-full min-w-0 flex-shrink-0 flex-col overflow-y-auto border-b border-neutral-100 lg:w-[280px] lg:min-w-[280px] lg:max-w-[280px] lg:flex-shrink-0 lg:border-b-0 lg:border-r">
        <div className="mega-sidebar__menu-cap">
          <div className="mega-sidebar__menu-cap-inner">
            <List size={20} weight="bold" aria-hidden />
            <span>{menuCapLabel}</span>
          </div>
        </div>
        {CATEGORIES.map((cat) => {
          const Icon = CATEGORY_MENU_ICONS[cat.id] || SquaresFour;
          const isHover = hoveredCategoryId === cat.id;
          const isMobileOpen = mobileExpandedId === cat.id;
          const subs = SUBMENU_BY_CATEGORY_ID[cat.id] || [];
          const title = getCategoryTitle(cat, lang);
          return (
            <div key={cat.id} className="border-b border-neutral-100 last:border-b-0">
              <button
                type="button"
                onMouseEnter={() => setHoveredCategoryId(cat.id)}
                onClick={() => {
                  setHoveredCategoryId(cat.id);
                  setMobileExpandedId((prev) => (prev === cat.id ? null : cat.id));
                }}
                className={`mega-sidebar__btn flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-medium transition-all duration-200 lg:rounded-none ${
                  isHover ? 'mega-sidebar__btn--on pl-[13px]' : 'mega-sidebar__btn--off border-transparent pl-4'
                }`}
              >
                <Icon
                  size={22}
                  weight={isHover ? 'fill' : 'duotone'}
                  className={`shrink-0 ${isHover ? 'mega-sidebar__cat-icon--on' : 'mega-sidebar__cat-icon--off'}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 leading-snug">{title}</span>
                <CaretRight
                  size={18}
                  weight="bold"
                  className={`mega-sidebar__chevron hidden shrink-0 lg:block ${isHover ? 'mega-sidebar__chevron-on' : 'text-neutral-300'}`}
                  aria-hidden
                />
                <span className="text-neutral-400 lg:hidden" aria-hidden>
                  {isMobileOpen ? '−' : '+'}
                </span>
              </button>
              {isMobileOpen && subs.length > 0 && (
                <div className="border-t border-neutral-100 bg-[#f5f5f5] px-3 py-2 lg:hidden">
                  <div className="grid grid-cols-1 gap-1">
                    <button
                      type="button"
                      onClick={() => selectCategoryAndClose(cat.key)}
                      className="rounded-lg px-3 py-2 text-left text-xs font-semibold text-[#1e3a5f] underline-offset-2 hover:underline"
                    >
                      {tl('View all in category', 'Voir tout dans la categorie', 'Ver todo en la categoria', 'عرض الكل في الفئة')}
                    </button>
                    {subs.map((sub, sidx) =>
                      sub.gridSpacer ? null : (
                      <button
                        key={`${sub.key}-${sidx}`}
                        type="button"
                        onClick={() =>
                          selectCategoryAndClose(cat.key, sub.navSearch ? '' : sub.key, sub.navSearch || '')
                        }
                        className="rounded-lg px-3 py-2 text-left text-sm text-neutral-700 transition-colors duration-200 hover:bg-white"
                      >
                        {getSubmenuLabel(sub, lang, tl, cat.key)}
                      </button>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="mega-content-panel hidden min-h-[300px] min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden border-l-0 p-4 sm:p-5 lg:flex lg:border-l lg:border-[#0c2340]/[0.07]">
        {hoveredCat && hoveredSubs.length > 0 ? (
          <CategoryMegaMenuRightColumn
            hoveredCat={hoveredCat}
            hoveredSubs={hoveredSubs}
            lang={lang}
            selectCategoryAndClose={selectCategoryAndClose}
            onNavigate={onNavigate}
            closePanel={() => setCategoriesPanelOpen(false)}
          />
        ) : (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 px-4 text-center lg:min-h-[280px]">
            <SquaresFour size={40} weight="duotone" className="mega-sidebar__empty-icon shrink-0" aria-hidden />
            <p className="max-w-[16rem] font-sans text-sm leading-relaxed text-neutral-500">
              {tl(
                'Hover a category on the left to see subcategories and featured items.',
                'Survolez une categorie a gauche pour afficher les sous-categories.',
                'Pase el cursor sobre una categoria a la izquierda para ver subcategorias.',
                'مرر مؤشر الفأرة فوق فئة على اليسار لعرض الفئات الفرعية.'
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const navLinkClass = (key) =>
    `shrink-0 whitespace-nowrap px-1 py-2 text-[9px] font-semibold uppercase tracking-[0.08em] transition-colors duration-200 rounded-md sm:px-1.5 sm:text-[10px] xl:px-2 xl:text-[11px] xl:tracking-[0.1em] ${
      currentPage === key
        ? 'text-[#0c2340]'
        : 'text-neutral-400 hover:text-neutral-800'
    }`;

  const utilityCluster = (
    <>
      <div className="hidden shrink-0 sm:flex sm:items-center">
        <label htmlFor="header-lang-desktop" className="sr-only">
          {tl('Language', 'Langue', 'Idioma', 'اللغة')}
        </label>
        <select
          id="header-lang-desktop"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          aria-label={tl('Language', 'Langue', 'Idioma', 'اللغة')}
          data-testid="language-select"
          className="h-9 min-h-[36px] w-[min(100%,8.25rem)] cursor-pointer appearance-none rounded-full border border-neutral-200/90 bg-white bg-[length:10px_6px] bg-[right_0.65rem_center] bg-no-repeat py-1.5 pl-2.5 pr-8 text-[12px] font-medium text-neutral-800 shadow-none transition-colors hover:border-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0c2340]/25 focus-visible:ring-offset-0 xl:w-[min(100%,9.75rem)] xl:text-[13px]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' d='M1 1.5L6 6l5-4.5'/%3E%3C/svg%3E")`,
          }}
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.flag} {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="inline-flex shrink-0 items-center gap-0 sm:gap-0.5">
        <button
          type="button"
          onClick={() => window.open(WORKSPACE_LOGIN_URL, '_blank', 'noopener,noreferrer')}
          className="hidden min-h-[44px] min-w-[44px] rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-[#0c2340] sm:inline-flex sm:items-center sm:justify-center"
          aria-label={t('authAccountAria')}
          title={t('authAccountAria')}
          data-testid="header-account"
        >
          <UserCircle size={22} weight="regular" />
        </button>

        <button
          type="button"
          onClick={() => onNavigate('rfq')}
          className="relative flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-[#0c2340]"
          data-testid="rfq-basket-button"
          aria-label={t('rfqBasket')}
        >
          <ShoppingCart size={22} weight="regular" />
          {items.length > 0 && (
            <span className="absolute right-0.5 top-0.5 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#0c2340] px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white">
              {items.length > 99 ? '99+' : items.length}
            </span>
          )}
        </button>
      </div>

      <button
        type="button"
        onClick={goToLiveShipmentTracking}
        className="group ml-1 hidden min-h-[44px] shrink-0 items-stretch overflow-hidden rounded-xl shadow-[0_8px_24px_-6px_rgba(12,35,64,0.35)] ring-1 ring-black/5 transition-transform hover:scale-[1.02] hover:shadow-[0_10px_28px_-4px_rgba(12,35,64,0.4)] active:scale-[0.98] lg:ml-2 lg:inline-flex 2xl:ml-4"
        data-testid="header-cta-shipment-tracking"
        aria-label={t('headerLiveShipmentTracking')}
      >
        <span className="hidden max-w-[12.5rem] items-center whitespace-normal bg-gradient-to-br from-[#0c2340] via-[#0f2d52] to-[#0a1838] px-3 py-2 text-left font-sans text-xs font-bold leading-tight tracking-tight text-white antialiased 2xl:flex">
          {t('headerLiveShipmentTracking')}
        </span>
        <span
          className="flex min-w-[44px] items-center justify-center bg-gradient-to-br from-[#0c2340] via-[#0f2d52] to-[#0a1838] px-2.5 py-2 text-white 2xl:min-w-[40px] 2xl:border-l 2xl:border-white/15 2xl:bg-gradient-to-b 2xl:from-[#f0f4fa] 2xl:to-[#e2e8f0] 2xl:px-2 2xl:py-1.5 2xl:text-[#0c2340]"
          aria-hidden
        >
          <MapTrifold size={18} weight="regular" className="opacity-95 xl:opacity-95" />
        </span>
      </button>
    </>
  );

  return (
    <header
      ref={headerRef}
      onMouseEnter={clearCategoriesCloseTimer}
      onMouseLeave={isLg ? scheduleCategoriesClose : undefined}
      className={`fixed ${testSiteBanner ? 'top-10' : 'top-0'} left-0 right-0 z-50 bg-white transition-[box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? 'border-neutral-200/90 shadow-[0_10px_40px_-16px_rgba(15,23,42,0.14)]'
          : 'border-neutral-200/55 shadow-[0_1px_0_rgba(15,23,42,0.05)]'
      }`}
      data-testid="main-header"
    >
      <div className="mx-auto max-w-[1920px] px-2.5 pl-[max(0.35rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))] sm:px-3 sm:pr-4 md:pr-5 lg:pl-3 lg:pr-4 xl:pl-4 xl:pr-5">
        <div className="flex min-w-0 flex-nowrap items-center gap-1 py-2 sm:gap-1.5 sm:py-2 lg:min-h-[112px] lg:items-center lg:gap-0.5 xl:min-h-[140px] xl:gap-1">
          <div className="flex shrink-0 items-center lg:min-h-[112px] lg:-ml-1 xl:min-h-[140px] xl:-ml-1.5">
            <button
              type="button"
              onClick={() => onNavigate('home')}
              className="-ml-0.5 flex items-center py-0.5 transition-opacity duration-200 hover:opacity-90 sm:-ml-0 lg:-ml-0.5"
              data-testid="logo-button"
            >
              <img
                src={SITE_LOGO_SRC}
                alt="DeMaxtore — Connecting Buyers with Turkish Suppliers"
                className="h-[4.25rem] w-auto max-h-[88px] max-w-[min(58vw,280px)] object-contain object-left sm:h-[5.256rem] sm:max-h-[113px] lg:h-[4.75rem] lg:max-h-[102px] lg:max-w-[min(34vw,260px)] xl:h-[5.76rem] xl:max-h-[124px] xl:max-w-[min(28vw,320px)] 2xl:max-w-[min(26vw,380px)]"
                decoding="async"
              />
            </button>
          </div>

          {/* Masaüstü: Categories + mega — orta sütun tam genişlikte (link aralığı sıkı) */}
          <div className="hidden min-w-0 flex-1 items-center lg:flex">
            <div className="flex w-full min-w-0 items-center justify-center">
              <nav
                className="header-desktop-nav flex w-full min-w-0 max-w-full flex-nowrap items-center justify-center gap-x-0.5 px-0.5 xl:gap-x-1"
                data-testid="desktop-nav"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (categoriesPanelOpen) {
                      scheduleCategoriesClose();
                    } else {
                      setMegaClosing(false);
                      setCategoriesPanelOpen(true);
                    }
                  }}
                  onMouseEnter={openCategoriesHover}
                  className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.08em] outline-none transition-all focus-visible:ring-2 focus-visible:ring-[#0c2340]/20 sm:gap-1 sm:px-2 sm:text-[10px] sm:tracking-[0.1em] xl:px-2.5 xl:text-[11px] ${
                    categoriesPanelOpen
                      ? 'border-[#0c2340]/40 bg-[#0c2340]/[0.09] text-[#0c2340] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]'
                      : currentPage === 'categories'
                        ? 'border-[#0c2340]/25 bg-[#0c2340]/[0.05] text-[#0c2340]'
                        : 'border-neutral-200/95 bg-neutral-50 text-neutral-600 hover:border-neutral-300 hover:bg-white hover:text-neutral-900'
                  }`}
                  data-testid="nav-categories-open"
                  aria-expanded={categoriesPanelOpen}
                  aria-haspopup="true"
                >
                  <List
                    size={14}
                    weight="bold"
                    className="shrink-0 text-[#0c2340]/85"
                    aria-hidden
                  />
                  <span>{t('categories')}</span>
                  <CaretDown
                    size={12}
                    weight="bold"
                    className={`shrink-0 text-[#0c2340]/70 transition-transform duration-200 ${
                      categoriesPanelOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>
                <div
                  className="relative shrink-0"
                  onMouseEnter={openSolutionsHover}
                  onMouseLeave={scheduleSolutionsClose}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (solutionsPanelOpen) {
                        scheduleSolutionsClose();
                      } else {
                        openSolutionsHover();
                      }
                    }}
                    className={`inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-md px-1 py-2 text-[9px] font-semibold uppercase tracking-[0.08em] transition-colors duration-200 sm:px-1.5 sm:text-[10px] sm:tracking-[0.1em] xl:px-2 xl:text-[11px] ${
                      solutionsPanelOpen
                        ? 'text-[#0c2340]'
                        : 'text-neutral-400 hover:text-neutral-800'
                    }`}
                    data-testid="nav-solutions"
                    aria-expanded={solutionsPanelOpen}
                    aria-haspopup="true"
                  >
                    <span>{t('solutions')}</span>
                    <CaretDown
                      size={12}
                      weight="bold"
                      className={`shrink-0 transition-transform duration-200 ${
                        solutionsPanelOpen ? 'rotate-180 text-[#0c2340]' : 'text-neutral-400'
                      }`}
                      aria-hidden
                    />
                  </button>
                  {solutionsPanelOpen && (
                    <div
                      className="absolute left-0 top-full z-[60] mt-2 overflow-hidden rounded-xl border border-neutral-200/90 bg-white shadow-[0_16px_48px_-12px_rgba(15,23,42,0.2)] ring-1 ring-black/[0.04]"
                      onMouseEnter={clearSolutionsCloseTimer}
                      onMouseLeave={scheduleSolutionsClose}
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <SolutionsNavPanel onItemClick={handleSolutionClick} />
                    </div>
                  )}
                </div>
                <div
                  className="relative shrink-0"
                  onMouseEnter={openProcurementHover}
                  onMouseLeave={scheduleProcurementClose}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (procurementPanelOpen) {
                        scheduleProcurementClose();
                      } else {
                        openProcurementHover();
                      }
                    }}
                    className={`inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-md px-1 py-2 text-[9px] font-semibold uppercase tracking-[0.08em] transition-colors duration-200 sm:px-1.5 sm:text-[10px] sm:tracking-[0.1em] xl:px-2 xl:text-[11px] ${
                      procurementPanelOpen
                        ? 'text-[#0c2340]'
                        : 'text-neutral-400 hover:text-neutral-800'
                    }`}
                    data-testid="nav-procurement-models"
                    aria-expanded={procurementPanelOpen}
                    aria-haspopup="true"
                  >
                    <span>{renderNavLabel(t('procurementModels'), t('procurementModelsShort'))}</span>
                    <CaretDown
                      size={12}
                      weight="bold"
                      className={`shrink-0 transition-transform duration-200 ${
                        procurementPanelOpen ? 'rotate-180 text-[#0c2340]' : 'text-neutral-400'
                      }`}
                      aria-hidden
                    />
                  </button>
                  {procurementPanelOpen && (
                    <div
                      className="absolute left-0 top-full z-[60] mt-2 overflow-hidden rounded-xl border border-neutral-200/90 bg-white shadow-[0_16px_48px_-12px_rgba(15,23,42,0.2)] ring-1 ring-black/[0.04]"
                      onMouseEnter={clearProcurementCloseTimer}
                      onMouseLeave={scheduleProcurementClose}
                    >
                      <ProcurementModelsNavPanel onItemClick={handleProcurementClick} />
                    </div>
                  )}
                </div>
                {centerNavItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onNavigate(item.key)}
                    className={navLinkClass(item.key)}
                    data-testid={`nav-${item.key}`}
                  >
                    {renderNavLabel(item.label, item.shortLabel)}
                  </button>
                ))}
              </nav>

              {/* Masaüstü: soldan kayan mega (lacivert + kırmızı vurgu) */}
              {categoriesPanelOpen && isLg && (
                <div
                  className={`header-mega-backdrop${megaClosing ? ' header-mega-backdrop--closing' : ''}`}
                  style={{ top: megaMenuTopPx }}
                  aria-hidden
                  onClick={scheduleCategoriesClose}
                />
              )}
              {categoriesPanelOpen && (
                <div
                  className={`header-mega-shell hidden lg:block${megaClosing ? ' header-mega-shell--closing' : ''}`}
                  style={{ top: megaMenuTopPx }}
                  onMouseEnter={clearCategoriesCloseTimer}
                  onMouseLeave={scheduleCategoriesClose}
                >
                  <div className="header-mega-shell__inner">{categoriesMegaMenuBody}</div>
                </div>
              )}
            </div>
          </div>

          {/* Sağ: dil, ikonlar (+ masaüstünde Live Shipment Tracking) */}
          <div className="ml-auto flex min-h-[44px] shrink-0 flex-nowrap items-center justify-end gap-x-1.5 sm:gap-2 lg:min-h-0">
            {utilityCluster}

            <button
              type="button"
              onClick={toggleMobileDrawer}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-2 text-neutral-700 transition-colors hover:bg-neutral-100 lg:hidden"
              data-testid="mobile-menu-toggle"
              aria-label={mobileDrawerOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileDrawerOpen}
            >
              <span className="relative block h-[14px] w-[18px] text-neutral-800">
                <span
                  className={`absolute left-0 top-0 h-[2px] w-full rounded-full bg-current transition-all duration-300 ${
                    mobileDrawerOpen ? 'translate-y-[6px] rotate-45' : ''
                  }`}
                />
                <span
                  className={`absolute left-0 top-[6px] h-[2px] w-full rounded-full bg-current transition-all duration-300 ${
                    mobileDrawerOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`absolute left-0 top-[12px] h-[2px] w-full rounded-full bg-current transition-all duration-300 ${
                    mobileDrawerOpen ? '-translate-y-[6px] -rotate-45' : ''
                  }`}
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      <Sheet
        modal={false}
        open={mobileDrawerOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isLg) setCategoriesPanelOpen(false);
        }}
      >
        <SheetContent
          side="left"
          onMouseEnter={clearCategoriesCloseTimer}
          onMouseLeave={scheduleCategoriesClose}
          className="categories-mega-sheet !w-[min(100vw,880px)] !max-w-[880px] flex h-full flex-col gap-0 border-r border-neutral-200/90 bg-white p-0 shadow-2xl sm:!w-[min(100vw,880px)] [&>button]:right-4 [&>button]:top-5 [&>button]:text-neutral-500"
        >
          <SheetHeader className="shrink-0 border-b border-neutral-100 bg-white px-6 pb-5 pt-6 text-left">
            <SheetTitle className="sr-only">
              {tl('Site menu and categories', 'Menu et categories', 'Menu y categorias', 'القائمة والفئات')}
            </SheetTitle>
            <img
              src={SITE_LOGO_SRC}
              alt="DeMaxtore"
              className="h-[5.04rem] w-auto max-h-[101px] max-w-[min(92vw,950px)] object-contain object-left sm:h-[5.256rem] sm:max-h-[107px]"
              decoding="async"
            />
          </SheetHeader>

          {/* Mobil: navbar + mega menü tek panelde */}
          <div className="flex shrink-0 flex-col border-b border-neutral-200/80 bg-[#f8fafc]">
            <nav className="px-4 py-3 sm:px-5" data-testid="mobile-menu" aria-label={tl('Main navigation', 'Navigation', 'Navegacion', 'التنقل')}>
              <div className="mb-1 rounded-xl border border-neutral-200/80 bg-white/60">
                <button
                  type="button"
                  onClick={() => setMobileSolutionsOpen((open) => !open)}
                  className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-neutral-700 transition-colors hover:bg-white hover:text-neutral-900"
                  data-testid="mobile-nav-solutions"
                  aria-expanded={mobileSolutionsOpen}
                >
                  <span>{t('solutions')}</span>
                  <CaretDown
                    size={16}
                    weight="bold"
                    className={`shrink-0 transition-transform ${mobileSolutionsOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {mobileSolutionsOpen && (
                  <div className="border-t border-neutral-100 px-1 pb-2 pt-1">
                    <SolutionsNavPanel
                      variant="mobile"
                      onItemClick={(item) => {
                        handleSolutionClick(item);
                        closeMobileDrawer();
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="mb-1 rounded-xl border border-neutral-200/80 bg-white/60">
                <button
                  type="button"
                  onClick={() => setMobileProcurementOpen((open) => !open)}
                  className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-neutral-700 transition-colors hover:bg-white hover:text-neutral-900"
                  data-testid="mobile-nav-procurement-models"
                  aria-expanded={mobileProcurementOpen}
                >
                  <span>{t('procurementModels')}</span>
                  <CaretDown
                    size={16}
                    weight="bold"
                    className={`shrink-0 transition-transform ${mobileProcurementOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {mobileProcurementOpen && (
                  <div className="border-t border-neutral-100 px-1 pb-2 pt-1">
                    <ProcurementModelsNavPanel
                      variant="mobile"
                      onItemClick={(item) => {
                        handleProcurementClick(item);
                        closeMobileDrawer();
                      }}
                    />
                  </div>
                )}
              </div>
              {centerNavItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    onNavigate(item.key);
                    closeMobileDrawer();
                  }}
                  className={`mb-1 block w-full rounded-xl px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide transition-colors last:mb-0 ${
                    currentPage === item.key
                      ? 'bg-[#0c2340]/10 text-[#0c2340]'
                      : 'text-neutral-700 hover:bg-white hover:text-neutral-900'
                  }`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="border-t border-neutral-200/80 px-4 pb-3 pt-2 sm:px-5">
              <label
                htmlFor="header-lang-mobile"
                className="mb-2 flex items-center gap-2 text-xs font-medium text-neutral-500"
              >
                <Globe size={14} weight="regular" aria-hidden />
                {tl('Language', 'Langue', 'Idioma', 'اللغة')}
              </label>
              <select
                id="header-lang-mobile"
                value={lang}
                onChange={(e) => {
                  setLang(e.target.value);
                }}
                aria-label={tl('Language', 'Langue', 'Idioma', 'اللغة')}
                data-testid="language-select-mobile"
                className="min-h-[44px] w-full cursor-pointer appearance-none rounded-xl border border-neutral-200/90 bg-white bg-[length:12px_8px] bg-[right_1rem_center] bg-no-repeat px-4 py-2.5 pr-11 text-left text-sm font-semibold text-neutral-800 shadow-sm transition-colors hover:border-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0c2340]/25"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' d='M1 1.5L6 6l5-4.5'/%3E%3C/svg%3E")`,
                }}
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.flag} {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => {
                window.open(WORKSPACE_LOGIN_URL, '_blank', 'noopener,noreferrer');
                closeMobileDrawer();
              }}
              className="flex w-full items-center gap-2 border-t border-neutral-200/80 px-4 py-3 text-sm font-medium text-neutral-600 hover:bg-white sm:px-5"
            >
              <UserCircle size={20} weight="regular" />
              {t('authAccountMenu')}
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{categoriesMegaMenuBody}</div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
